import { Popover } from "@cmp-src/overlay/Popover/Popover";
import { Calendar } from "@cmp-src/primitives/calendar";
import { dateParseISO } from "@gettactic/helpers/src/date-time/dateParseISO";
import { useWeekStartsOn } from "@gettactic/hooks";
import { addDays, endOfWeek, format, startOfWeek } from "date-fns";

import React from "react";
import type { DateRange } from "react-day-picker";
import { ArrowsGroup } from "../ArrowsGroup/ArrowsGroup";
import { CalendarTrigger } from "./CalendarTrigger";

export interface WeekSwitchProps {
  weekDate: string;
  setWeekDate: (date: string) => void;
  nextWeek: () => void;
  prevWeek: () => void;
  showArrow?: boolean;
}

export const WeekSwitch = ({
  weekDate,
  nextWeek,
  prevWeek,
  setWeekDate,
  showArrow = false
}: WeekSwitchProps) => {
  const [isOpen, setOpen] = React.useState(false);
  const weekStartsOn = useWeekStartsOn();

  const _startOfWeek = React.useMemo(
    // dateParseISO parses the date string as local midnight; new Date() would
    // treat it as UTC and shift the week back a day for viewers west of UTC
    () => startOfWeek(dateParseISO(weekDate), { weekStartsOn }),
    [weekDate, weekStartsOn]
  );
  const _endOfWeek = React.useMemo(
    () => endOfWeek(_startOfWeek, { weekStartsOn }),
    [_startOfWeek, weekStartsOn]
  );

  const weekRange = React.useMemo(
    () => ({ from: _startOfWeek, to: _endOfWeek }),
    [_startOfWeek, _endOfWeek]
  );

  const [selectedWeek, setSelectedWeek] = React.useState<DateRange | undefined>(
    weekRange
  );

  // Keep the popover highlight in sync when the week changes from outside the
  // calendar (arrow navigation, or the week start setting resolving async)
  React.useEffect(() => {
    setSelectedWeek(weekRange);
  }, [weekRange]);

  const calendarContent = (
    <Calendar
      className="border-0"
      required={true}
      mode="single"
      weekHover={true}
      showOutsideDays
      // @ts-expect-error The Calendar component expects a different type, but this works as intended
      selected={selectedWeek}
      disableApplyButtons={true}
      modifiers={{
        selected: selectedWeek,
        range_start: selectedWeek?.from,
        range_end: selectedWeek?.to,
        range_middle: (date: Date) => {
          if (!selectedWeek?.from || !selectedWeek?.to) return false;
          const time = date.getTime();
          // Include start and end dates in the range_middle styling
          return (
            time >= selectedWeek.from.getTime() &&
            time <= selectedWeek.to.getTime()
          );
        }
      }}
      onDayClick={(day, modifiers) => {
        if (modifiers.selected) {
          setSelectedWeek(undefined); // clear the selection if the day is already selected
          return;
        }
        const from = startOfWeek(day, { weekStartsOn });
        const to = endOfWeek(day, { weekStartsOn });
        setSelectedWeek({
          from,
          to
        });
        setWeekDate(format(addDays(from, 1), "yyyy-MM-dd"));
      }}
      onSelect={(range: DateRange) => {
        setSelectedWeek(range);

        setWeekDate(format(addDays(range.from ?? new Date(), 1), "yyyy-MM-dd"));
        setTimeout(() => setOpen(false), 100);
      }}
    />
  );

  return (
    <div className="mb-2 flex w-full flex-row items-center justify-between gap-1 align-center">
      <div className="flex-grow">
        <Popover
          open={isOpen}
          onOpenChange={setOpen}
          content={calendarContent}
          bodyProps={{ className: "p-0" }}
          contentProps={{
            align: "start",
            showArrow
          }}
        >
          <CalendarTrigger
            dates={weekRange}
            separator={<>&nbsp;—&nbsp;</>}
            variant="outlined"
            buttonProps={{
              onClick: () => setOpen(!isOpen),
              className:
                "w-full rounded-lg hover:bg-neutral-50/50 hover:shadow-md hover:border-neutral-200 hover:transition-all hover:duration-200"
            }}
          />
        </Popover>
      </div>
      <div>
        <ArrowsGroup
          leftProps={{
            onClick: prevWeek
          }}
          rightProps={{
            onClick: nextWeek
          }}
        />
      </div>
    </div>
  );
};
